import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { money, periodLabel, shortDate } from "@/lib/format";
import { Download, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import ProductBreakdown from "@/components/ProductBreakdown";
import PartnerSignupLinkCard from "@/components/PartnerSignupLinkCard";

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/partner/dashboard"),
      api.get("/partner/analytics/products"),
    ])
      .then(([d, a]) => { setData(d.data); setAnalytics(a.data); })
      .catch(() => toast.error("Failed to load your dashboard"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500 text-sm">Loading…</div>;
  if (!data) return <div className="text-slate-500">Nothing to show yet.</div>;
  const k = data.kpis;

  return (
    <div className="space-y-14">
      <header>
        <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-3 inline-flex items-center gap-2">
          Your earnings
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-[10px] normal-case tracking-normal font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
            Just your account
          </span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium">
          {data.partner?.name || "Partner"}
        </h1>
        <p className="text-slate-400 mt-3 max-w-xl">
          Here&apos;s what {data.brand_name} owes you across your referral book.
          Everything below is ex-GST — GST is added on payout. Only you and {data.brand_name} admins can see these figures.
        </p>
      </header>

      {/* Big hero KPI */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-2xl border border-slate-800/80 bg-slate-900 p-8 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Year to date</div>
            <div className="font-mono text-5xl md:text-6xl font-medium text-cyan-500 mt-3 tracking-tighter">
              {money(k.ytd_ex_gst)}
            </div>
            <div className="text-sm text-slate-400 mt-2 font-mono">
              {k.ytd_event_count} commissionable events
            </div>
            <div className="grid grid-cols-3 gap-6 mt-10 pt-6 border-t border-slate-800">
              <MiniStat label="This month" value={money(k.mtd_ex_gst)} sub={`${k.mtd_event_count} events`} />
              <MiniStat label="Lifetime" value={money(k.lifetime_ex_gst)} sub={`${k.lifetime_event_count} events`} />
              <MiniStat label="Pending payout" value={money(k.pending_ex_gst)} sub="ex-GST" />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-slate-800/80 bg-slate-950 text-white p-8 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-[0.08] pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">Lifetime paid</div>
            <div className="font-mono text-4xl font-medium mt-3">{money(k.total_paid_lifetime)}</div>
            <div className="text-sm text-slate-500 mt-2">Sum of payouts marked paid</div>
            <div className="mt-8 pt-6 border-t border-slate-700/40">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Trend · last 6 months</div>
              <MiniTrend trend={data.trend} />
            </div>
          </div>
        </div>
      </section>

      {/* Partner-specific customer signup link */}
      {data.partner?.code && (
        <PartnerSignupLinkCard
          partnerCode={data.partner.code}
          partnerName={data.partner.name}
          transferUpstream={data.partner.transfer_upstream_name}
        />
      )}

      {/* One-click branded PDF: "How to sign customers up" — renders the
          admin-editable template with THIS partner's code/name/URLs
          substituted, so a partner can email the file straight to
          their sales team without editing anything. */}
      {data.partner?.code && (
        <section>
          <a
            data-testid="partner-signup-guide-download"
            href={`${API_BASE}/partner/onboarding-guide.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm px-4 py-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 hover:bg-cyan-500/10 text-cyan-200"
          >
            <Download className="w-4 h-4" />
            Download the "How to sign customers up" guide (PDF)
          </a>
        </section>
      )}

      {/* Earnings by product */}
      {analytics && (
        <section>
          <ProductBreakdown
            products={analytics.products}
            total={analytics.total_commission}
            title="Earnings by product · lifetime"
            subtitle="Where your commissions are coming from — the higher the slice, the harder that category is pulling for you."
            emptyLabel="No commissionable events yet."
            testId="partner-product-breakdown"
          />
        </section>
      )}

      {/* Payouts */}
      <section>
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-500 font-semibold mb-2">Payments</div>
            <h2 className="font-display text-3xl tracking-tight">Your payouts</h2>
          </div>
        </div>
        <div className="rounded-xl border border-slate-800/80 bg-slate-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-6 py-3">Period</th>
                <th className="text-right px-6 py-3">Events</th>
                <th className="text-right px-6 py-3">Ex-GST</th>
                <th className="text-right px-6 py-3">GST</th>
                <th className="text-right px-6 py-3">Total</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-left px-6 py-3">Paid on</th>
                <th className="text-right px-6 py-3">Report</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100" data-testid="partner-payouts-tbody">
              {data.payouts.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-500">No payouts yet. Once your commissions are rolled up, they&apos;ll appear here.</td></tr>
              )}
              {data.payouts.map((p) => (
                <tr key={p.id} className="hover:bg-slate-950/50">
                  <td className="px-6 py-4 font-medium">{periodLabel(p.period)}</td>
                  <td className="px-6 py-4 text-right font-mono">{p.event_count}</td>
                  <td className="px-6 py-4 text-right font-mono">{money(p.amount_ex_gst)}</td>
                  <td className="px-6 py-4 text-right font-mono text-slate-400">{money(p.amount_gst)}</td>
                  <td className="px-6 py-4 text-right font-mono font-medium text-cyan-400">{money(p.amount_total)}</td>
                  <td className="px-6 py-4"><StatusBadge status={p.status} /></td>
                  <td className="px-6 py-4 font-mono text-xs text-slate-400">{p.paid_at ? shortDate(p.paid_at) : "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <a
                        data-testid={`payout-pdf-${p.id}`}
                        href={`${API_BASE}/partner/payouts/${p.id}/report.pdf`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-800 hover:bg-slate-950"
                      >
                        <Download className="w-3 h-3" /> PDF
                      </a>
                      <Link
                        to={`/portal/payouts/${p.id}`}
                        data-testid={`payout-detail-${p.id}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-950 text-white hover:bg-slate-800"
                      >
                        Detail <ArrowUpRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-mono text-xl font-medium mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function MiniTrend({ trend }) {
  const max = Math.max(1, ...trend.map((t) => t.amount));
  return (
    <div>
      <div className="h-16 flex items-end gap-2">
        {trend.map((t) => {
          const h = Math.max((t.amount / max) * 100, 3);
          return (
            <div key={t.period} className="flex-1 relative group">
              <div className="w-full rounded-t bg-cyan-500/80" style={{ height: `${h}%` }} />
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-slate-50 text-xs font-mono px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {money(t.amount)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex gap-2 text-xs text-slate-500">
        {trend.map((t) => (<div key={t.period} className="flex-1 text-center font-mono">{t.period.slice(5)}</div>))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: "bg-cyan-500 text-white border-cyan-500",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    on_hold: "bg-slate-800 text-slate-300 border-slate-800",
  };
  const cls = map[status] || "bg-slate-800 text-slate-300 border-slate-800";
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${cls} font-medium`}>{status}</span>;
}
